/**
 * usePredictions Hook
 *
 * Applies a selected prediction algorithm to raw team calculator data.
 * Returns drivers and constructors with computed predictions.
 */

import { useEffect, useMemo, useState } from "react";
import {
  algorithmRegistry,
  type PredictedConstructor,
  type PredictedDriver,
} from "../lib/algorithms";
import { getSeasonPricing } from "../lib/constants";
import { calculatePPMPriceChange } from "../lib/ppm-price-change";
import type {
  TeamCalculatorConstructor,
  TeamCalculatorData,
  TeamCalculatorDriver,
  TeamCalculatorRaceContext,
} from "./use-f1-data";

// Schedule work in idle time, falling back to setTimeout for SSR/older browsers
function scheduleIdle(cb: () => void): number {
  if (typeof requestIdleCallback !== "undefined") {
    return requestIdleCallback(cb, { timeout: 2000 });
  }
  return setTimeout(cb, 0) as unknown as number;
}
function cancelIdle(id: number): void {
  if (typeof cancelIdleCallback !== "undefined") {
    cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
}

export interface PredictionsResult {
  drivers: PredictedDriver[];
  constructors: PredictedConstructor[];
  algorithmId: string;
  algorithmName: string;
  /** true when showing predictions (any stage except after_race with completed data) */
  isPredictionMode: boolean;
}

/**
 * Builds a deterministic zero-prediction result from raw data.
 * Used as the initial state on both SSR and client hydration to prevent
 * React error #418 (text content mismatch) caused by Math.random() in
 * Monte Carlo simulation producing different values on server vs. client.
 */
function buildRawResult(
  data: TeamCalculatorData | undefined,
  algorithmId: string,
): PredictionsResult {
  if (!data) {
    return {
      drivers: [],
      constructors: [],
      algorithmId,
      algorithmName: "",
      isPredictionMode: true,
    };
  }
  return {
    drivers: data.drivers.map((driver) => ({
      id: driver.id,
      driverSlug: driver.driverSlug,
      code: driver.code,
      permanentNumber: driver.permanentNumber,
      fullName: driver.fullName,
      constructorSlug: driver.constructorSlug,
      constructorName: driver.constructorName,
      currentPrice: driver.currentPrice,
      totalSeasonPoints: driver.totalSeasonPoints,
      predictedRacePoints: 0,
      predictedPriceChange: 0,
      confidence: 0,
      stdDev: 0,
      dnfProb: 0,
      raceHistory: driver.raceHistory,
    })),
    constructors: data.constructors.map((ctor) => ({
      id: ctor.id,
      name: ctor.name,
      slug: ctor.slug,
      code: ctor.code,
      currentPrice: ctor.currentPrice,
      totalSeasonPoints: ctor.totalSeasonPoints,
      predictedRacePoints: 0,
      predictedPriceChange: 0,
      confidence: 0,
      raceHistory: ctor.raceHistory,
    })),
    algorithmId,
    algorithmName: "",
    isPredictionMode: true,
  };
}

/**
 * Runs the full prediction algorithm (including Monte Carlo simulation).
 * Must only be called client-side after hydration.
 */
function computePredictions(
  data: TeamCalculatorData | undefined,
  algorithmId: string,
): PredictionsResult {
  if (!data) {
    return {
      drivers: [],
      constructors: [],
      algorithmId,
      algorithmName: "",
      isPredictionMode: true,
    };
  }

  const algorithm =
    algorithmRegistry.get(algorithmId) ?? algorithmRegistry.getDefault();

  if (!algorithm) {
    console.warn(`Algorithm "${algorithmId}" not found, using empty result`);
    return {
      drivers: [],
      constructors: [],
      algorithmId,
      algorithmName: "",
      isPredictionMode: true,
    };
  }

  // Check if we're at after_race stage with completed data — use actuals
  const isAfterRace = data.raceContext.currentStage === "after_race";
  const hasAnyCompleted =
    isAfterRace &&
    (data.drivers.some((d) => d.isCompleted) ||
      data.constructors.some((c) => c.isCompleted));
  const isPredictionMode = !hasAnyCompleted;

  const raceContext: TeamCalculatorRaceContext = {
    season: data.raceContext.season,
    raceName: data.raceContext.raceName,
    raceRound: data.raceContext.raceRound,
    totalRacesInSeason: data.raceContext.totalRacesInSeason,
    isSprintWeekend: data.raceContext.isSprintWeekend,
    currentStage: data.raceContext.currentStage,
  };

  // Build constructor → drivers map for session average computation.
  // Used by convertConstructorToAlgorithmFormat to inject session averages.
  const driversByConstructor = new Map<string, TeamCalculatorDriver[]>();
  for (const d of data.drivers) {
    const list = driversByConstructor.get(d.constructorSlug) ?? [];
    list.push(d);
    driversByConstructor.set(d.constructorSlug, list);
  }

  // Season pricing rules (price floor + tier threshold) for price-change
  // calculations — both vary per season; floor is derived from the lowest
  // observed price when the season's value isn't confirmed yet.
  const driverPricing = getSeasonPricing(
    raceContext.season,
    data.drivers.map((d) => d.currentPrice),
  );
  const constructorPricing = getSeasonPricing(
    raceContext.season,
    data.constructors.map((c) => c.currentPrice),
  );

  const drivers: PredictedDriver[] = data.drivers.map((driver) => {
    if (!isPredictionMode && driver.isCompleted) {
      return {
        id: driver.id,
        driverSlug: driver.driverSlug,
        code: driver.code,
        permanentNumber: driver.permanentNumber,
        fullName: driver.fullName,
        constructorSlug: driver.constructorSlug,
        constructorName: driver.constructorName,
        currentPrice: driver.currentPrice,
        totalSeasonPoints: driver.totalSeasonPoints,
        predictedRacePoints: driver.currentRacePoints ?? 0,
        predictedPriceChange: driver.actualPriceChange ?? 0,
        confidence: 1,
        stdDev: 0,
        dnfProb: 0,
        raceHistory: driver.raceHistory,
      };
    }

    const prediction = algorithm.predictDriver(
      convertDriverToAlgorithmFormat(driver),
      convertRaceContextToAlgorithmFormat(raceContext),
    );

    const driverHistory = driver.raceHistory;
    const driverR1 =
      driverHistory.length >= 1
        ? driverHistory[driverHistory.length - 1]
        : null;
    const driverR2 =
      driverHistory.length >= 2
        ? driverHistory[driverHistory.length - 2]
        : null;
    const driverPpmResult = calculatePPMPriceChange(
      {
        pointsRMinus2: driverR2?.totalPoints ?? null,
        priceRMinus2: driverR2?.price ?? null,
        pointsRMinus1: driverR1?.totalPoints ?? null,
        priceRMinus1: driverR1?.price ?? null,
        predictedPoints: prediction.predictedPoints,
        currentPrice: driver.currentPrice,
      },
      driverPricing,
    );

    return {
      id: driver.id,
      driverSlug: driver.driverSlug,
      code: driver.code,
      permanentNumber: driver.permanentNumber,
      fullName: driver.fullName,
      constructorSlug: driver.constructorSlug,
      constructorName: driver.constructorName,
      currentPrice: driver.currentPrice,
      totalSeasonPoints: driver.totalSeasonPoints,
      predictedRacePoints: prediction.predictedPoints,
      predictedPriceChange: driverPpmResult.expectedPriceChange,
      confidence: prediction.confidence,
      stdDev: prediction.stdDev,
      dnfProb: prediction.dnfProb,
      raceHistory: driver.raceHistory,
    };
  });

  const constructors: PredictedConstructor[] = data.constructors.map((ctor) => {
    if (!isPredictionMode && ctor.isCompleted) {
      return {
        id: ctor.id,
        name: ctor.name,
        slug: ctor.slug,
        code: ctor.code,
        currentPrice: ctor.currentPrice,
        totalSeasonPoints: ctor.totalSeasonPoints,
        predictedRacePoints: ctor.currentRacePoints ?? 0,
        predictedPriceChange: ctor.actualPriceChange ?? 0,
        confidence: 1,
        raceHistory: ctor.raceHistory,
      };
    }

    const prediction = algorithm.predictConstructor(
      convertConstructorToAlgorithmFormat(ctor, driversByConstructor),
      convertRaceContextToAlgorithmFormat(raceContext),
    );

    const ctorHistory = ctor.raceHistory;
    const ctorR1 =
      ctorHistory.length >= 1 ? ctorHistory[ctorHistory.length - 1] : null;
    const ctorR2 =
      ctorHistory.length >= 2 ? ctorHistory[ctorHistory.length - 2] : null;
    const ctorPpmResult = calculatePPMPriceChange(
      {
        pointsRMinus2: ctorR2?.totalPoints ?? null,
        priceRMinus2: ctorR2?.price ?? null,
        pointsRMinus1: ctorR1?.totalPoints ?? null,
        priceRMinus1: ctorR1?.price ?? null,
        predictedPoints: prediction.predictedPoints,
        currentPrice: ctor.currentPrice,
      },
      constructorPricing,
    );

    return {
      id: ctor.id,
      name: ctor.name,
      slug: ctor.slug,
      code: ctor.code,
      currentPrice: ctor.currentPrice,
      totalSeasonPoints: ctor.totalSeasonPoints,
      predictedRacePoints: prediction.predictedPoints,
      predictedPriceChange: ctorPpmResult.expectedPriceChange,
      confidence: prediction.confidence,
      raceHistory: ctor.raceHistory,
    };
  });

  return {
    drivers,
    constructors,
    algorithmId: algorithm.metadata.id,
    algorithmName: algorithm.metadata.name,
    isPredictionMode,
  };
}

/**
 * Hook that applies a prediction algorithm to raw team calculator data.
 *
 * Uses useState + useEffect with requestIdleCallback to avoid both:
 * 1. React hydration error #418 — Monte Carlo uses Math.random() which
 *    differs server vs client. Initial state uses deterministic buildRawResult.
 * 2. High TBT — Monte Carlo (~400ms) is deferred to idle time via
 *    requestIdleCallback, keeping it out of the Total Blocking Time window.
 */
export function usePredictions(
  data: TeamCalculatorData | undefined,
  algorithmId: string,
): PredictionsResult {
  const [result, setResult] = useState<PredictionsResult>(() =>
    buildRawResult(data, algorithmId),
  );

  useEffect(() => {
    // Defer heavy Monte Carlo computation to idle time (off TBT window)
    const id = scheduleIdle(() => {
      setResult(computePredictions(data, algorithmId));
    });
    return () => cancelIdle(id);
  }, [data, algorithmId]);

  return result;
}

/**
 * Convert hook driver type to algorithm driver type.
 * These are structurally the same, but we keep them separate for clarity.
 */
function convertDriverToAlgorithmFormat(
  driver: TeamCalculatorDriver,
): import("../lib/algorithms").TeamCalculatorDriver {
  return {
    id: driver.id,
    driverSlug: driver.driverSlug,
    code: driver.code,
    permanentNumber: driver.permanentNumber,
    fullName: driver.fullName,
    constructorSlug: driver.constructorSlug,
    constructorName: driver.constructorName,
    currentPrice: driver.currentPrice,
    totalSeasonPoints: driver.totalSeasonPoints,
    raceHistory: driver.raceHistory,
    // Live session positions for stage-aware predictions
    fp1Position: driver.fp1Position,
    fp2Position: driver.fp2Position,
    fp3Position: driver.fp3Position,
    sprintQualiPosition: driver.sprintQualiPosition,
    sprintPosition: driver.sprintPosition,
    qualiPosition: driver.qualiPosition,
  };
}

/**
 * Compute average session position across all drivers in a constructor.
 * ALWAYS substitutes P20 for null (null = driver got no time, not missing data).
 * Returns null only when the drivers array is empty (constructor has no drivers).
 */
function avgPosForConstructor(
  values: (number | null | undefined)[],
): number | null {
  if (values.length === 0) return null;
  // null/undefined = no time set in that session → penalise as P20
  const withPenalty = values.map((v) => v ?? 20);
  return withPenalty.reduce((a, b) => a + b, 0) / withPenalty.length;
}

/**
 * Convert hook constructor type to algorithm constructor type.
 * Injects session averages computed from the constructor's drivers.
 */
function convertConstructorToAlgorithmFormat(
  ctor: TeamCalculatorConstructor,
  driversByConstructor: Map<string, TeamCalculatorDriver[]>,
): import("../lib/algorithms").TeamCalculatorConstructor {
  const ctorDrivers = driversByConstructor.get(ctor.slug) ?? [];
  return {
    id: ctor.id,
    name: ctor.name,
    slug: ctor.slug,
    currentPrice: ctor.currentPrice,
    totalSeasonPoints: ctor.totalSeasonPoints,
    raceHistory: ctor.raceHistory,
    fp1AvgPosition: avgPosForConstructor(ctorDrivers.map((d) => d.fp1Position)),
    fp2AvgPosition: avgPosForConstructor(ctorDrivers.map((d) => d.fp2Position)),
    fp3AvgPosition: avgPosForConstructor(ctorDrivers.map((d) => d.fp3Position)),
    sprintQualiAvgPosition: avgPosForConstructor(
      ctorDrivers.map((d) => d.sprintQualiPosition),
    ),
    sprintAvgPosition: avgPosForConstructor(
      ctorDrivers.map((d) => d.sprintPosition),
    ),
    qualiAvgPosition: avgPosForConstructor(
      ctorDrivers.map((d) => d.qualiPosition),
    ),
  };
}

/**
 * Convert hook race context type to algorithm race context type.
 */
function convertRaceContextToAlgorithmFormat(
  raceContext: TeamCalculatorRaceContext,
): import("../lib/algorithms").TeamCalculatorRaceContext {
  return {
    season: raceContext.season,
    raceName: raceContext.raceName,
    raceRound: raceContext.raceRound,
    totalRacesInSeason: raceContext.totalRacesInSeason,
    isSprintWeekend: raceContext.isSprintWeekend,
    currentStage: raceContext.currentStage,
  };
}

/**
 * Get all available algorithms for display.
 */
export function useAlgorithms() {
  return useMemo(() => {
    return algorithmRegistry.getAllMetadata();
  }, []);
}

/**
 * Get the default algorithm ID.
 */
export function useDefaultAlgorithmId() {
  return useMemo(() => {
    return algorithmRegistry.getDefaultId();
  }, []);
}
